import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  salespersonsApi,
  type CreateSalespersonRequest,
  type SalespersonParams,
  type UpdateSalespersonRequest,
} from '@/apis/salespersons';
import { QUERY_KEYS } from '@/constants';

export const useSalespersonsQuery = (params?: SalespersonParams, enabled = true) => {
  const merged = { isSalesperson: true, ...params };
  return useQuery({
    queryKey: [QUERY_KEYS.SALESPERSONS, merged],
    queryFn: () => salespersonsApi.getAll(merged),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — prevents refetch on dialog reopen
  });
};

export const useSalespersonQuery = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SALESPERSONS, id],
    queryFn: () => salespersonsApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateSalesperson = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSalespersonRequest) => salespersonsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SALESPERSONS] });
      toast.success('Salesperson created successfully');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to create salesperson');
    },
  });
};

export const useUpdateSalesperson = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSalespersonRequest) => salespersonsApi.update(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData([QUERY_KEYS.SALESPERSONS, id], updated);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SALESPERSONS] });
      toast.success('Salesperson updated successfully');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to update salesperson');
    },
  });
};

export const useDeleteSalesperson = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => salespersonsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SALESPERSONS] });
      toast.success('Salesperson deleted successfully');
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || 'Failed to delete salesperson');
    },
  });
};
